import { useState, useRef, useEffect } from "react";
import { BRAND, Section } from "../brand";
import AudioPlayer from "../AudioPlayer";

const CASE_STUDIES = [
  {
    id: "kyu",
    category: "Fine Dining",
    emoji: "🔥",
    accent: "#C5A258",
    bg: "#1A1A1A",
    cardBg: "linear-gradient(135deg, #1A1A1A, #2A2218)",
    textColor: "#F5F0E8",
    subtextColor: "rgba(245,240,232,0.45)",
    borderColor: "rgba(197,162,88,0.2)",
    name: "KYU",
    subtitle: "Wood-Fired Asian · 4 Locations",
    location: "Miami · NYC · Las Vegas · CDMX",
    locations: 4,
    heroMetric: { value: "$960K+", label: "revenue facilitated by AI" },
    rotatingMetrics: [
      { value: "$960K+", label: "Revenue Facilitated", sub: "4,000 reservations × 3 guests × $80 avg check" },
      { value: "10,000", label: "Calls Handled by AI", sub: "across Miami + NYC in 4 months — zero voicemails" },
      { value: "4,000+", label: "Reservations Serviced", sub: "every reservation call answered, booked, or linked" },
      { value: "87.3%", label: "After-Hours Coverage", sub: "calls captured while the restaurant was closed" },
    ],
    timeline: [
      { week: "Week 1", event: "Anna trained on menus + Resy + OpenTable" },
      { week: "Week 3", event: "Live at KYU Miami + NYC — after-hours on" },
      { week: "Month 2", event: "4,500+ calls handled, peak 5-7pm covered" },
      { week: "Month 4", event: "10,000 calls, 98.7% autonomous, zero voicemails" },
    ],
    audioFile: "/audio/case-studies/kyu.mp3",
    audioLabel: "Happy Hour inquiry — KYU Miami",
    audioDuration: "1:38",
    bgImage: "/case-studies/bg/kyu.png",
    logo: "/case-studies/logos/kyu.png",
  },
  {
    id: "baires",
    category: "Full-Service Steakhouse",
    emoji: "🥩",
    accent: "#D4AF37",
    bg: "#0D0D0D",
    cardBg: "linear-gradient(135deg, #0D0D0D, #1A1208)",
    textColor: "#FFFFFF",
    subtextColor: "rgba(255,255,255,0.45)",
    borderColor: "rgba(212,175,55,0.2)",
    name: "Baires Grill",
    subtitle: "Argentine Steakhouse · 9 Locations across US",
    location: "Miami · Broward · Orlando · NYC",
    locations: 9,
    heroMetric: { value: "$2M+", label: "revenue facilitated by AI" },
    rotatingMetrics: [
      { value: "$2M+", label: "Revenue Facilitated", sub: "11,000 reservations × 2.5 guests × $75 avg check" },
      { value: "26,000+", label: "Calls Answered in 6 Months", sub: "across 8 Florida locations — zero voicemails" },
      { value: "11,000+", label: "Reservation Calls Serviced", sub: "41% of volume — each one booked, routed, or linked" },
      { value: "4,000", label: "Spanish-Language Calls", sub: "16% of volume handled natively in Spanish" },
    ],
    timeline: [
      { week: "Week 1", event: "Lucy trained on menu + OpenTable + Spanish" },
      { week: "Month 1", event: "Live at Doral, Sunny Isles, Brickell + Weston" },
      { week: "Month 4", event: "Expanded to Coral Gables, Miami Beach, Las Olas" },
      { week: "Month 6", event: "Orlando online — 26K calls across all 8 locations" },
    ],
    audioFile: "/audio/case-studies/baires.mp3",
    audioLabel: "Reservation + large party inquiry — Baires Brickell",
    audioDuration: "1:24",
    bgImage: "/case-studies/bg/baires.png",
    logo: "/case-studies/logos/baires.svg",
  },
  {
    id: "rreal",
    category: "Fast Casual",
    emoji: "🌮",
    accent: "#FF3E7A",
    bg: "#0D0D0D",
    cardBg: "linear-gradient(135deg, #0D0D0D, #1A0A12)",
    textColor: "#F5F0E8",
    subtextColor: "rgba(245,240,232,0.45)",
    borderColor: "rgba(255,62,122,0.2)",
    name: "Rreal Tacos",
    subtitle: "Mexican Street Food · 12 Locations",
    location: "Atlanta Metro Area",
    locations: 12,
    heroMetric: { value: "+410%", label: "increase in catering orders" },
    rotatingMetrics: [
      { value: "+410%", label: "Catering Orders Increase", sub: "AI captures every inquiry and sends menus via SMS" },
      { value: "$148K", label: "Monthly Revenue Assisted", sub: "from takeout orders, catering & reservations captured by AI" },
      { value: "100%", label: "Calls Answered", sub: "up from 52% — zero missed calls during lunch rush" },
      { value: "8,400+", label: "SMS Links Sent / Month", sub: "ordering, catering menus & location info" },
    ],
    timeline: [
      { week: "Week 1", event: "AI trained on 12 menus + ordering app" },
      { week: "Week 2", event: "Live — takeout & FAQ calls flowing" },
      { week: "Month 1", event: "30K calls, 4,200 takeout orders via app" },
      { week: "Month 3", event: "Catering up 410% — 12/mo to 60+" },
    ],
    audioFile: "/audio/case-studies/rreal.mp3",
    audioLabel: "Large party reservation — Rreal Tacos Midtown",
    audioDuration: "1:12",
    bgImage: "/case-studies/bg/rreal.png",
    logo: "/case-studies/logos/rreal.svg",
  },
  {
    id: "mangos",
    category: "Restaurant + Nightlife",
    emoji: "🌴",
    accent: "#B87333",
    bg: "#0A0A0A",
    cardBg: "linear-gradient(135deg, #0A0A0A, #0F1A12)",
    textColor: "#FFFFFF",
    subtextColor: "rgba(255,255,255,0.45)",
    borderColor: "rgba(184,115,51,0.2)",
    name: "Mango's Tropical Café",
    subtitle: "Eatertainment · 2 Locations",
    location: "South Beach · Orlando",
    locations: 2,
    heroMetric: { value: "$1.2M+", label: "revenue facilitated by AI" },
    rotatingMetrics: [
      { value: "$1.2M+", label: "Revenue Facilitated", sub: "4,700 reservations × 3 guests × $87 avg check" },
      { value: "14,600+", label: "Calls Answered in 7 Months", sub: "across South Beach + Orlando — 24/7, show nights included" },
      { value: "1,650", label: "Dinner Show Inquiries", sub: "showtimes, cover, dress code — every call answered" },
      { value: "1,300+", label: "Spanish-Language Calls", sub: "bilingual service across both flagship venues" },
    ],
    timeline: [
      { week: "Week 1", event: "Nacho trained on menus + show times + SevenRooms + Spanish" },
      { week: "Month 1", event: "Live at South Beach + Orlando — 24/7 coverage" },
      { week: "Month 4", event: "8,000+ calls handled, Fri-Sat peak covered" },
      { week: "Month 7", event: "14,600 calls, 64% autonomous, zero voicemails" },
    ],
    audioFile: "/audio/case-studies/mangos.mp3",
    audioLabel: "Dinner show + reservation inquiry — Mango's South Beach",
    audioDuration: "1:45",
    bgImage: "/case-studies/bg/mangos.png",
    logo: "/case-studies/logos/mangos.png",
  },
  {
    id: "bella",
    category: "Hospitality Group",
    emoji: "🍝",
    accent: "#3B6B4F",
    bg: "#1A1A1A",
    cardBg: "linear-gradient(135deg, #1A1A1A, #1A1D18)",
    textColor: "#F5EDE3",
    subtextColor: "rgba(245,237,227,0.45)",
    borderColor: "rgba(59,107,79,0.25)",
    name: "Bella Miami Beach",
    subtitle: "Think Hospitality · 20+ Venues",
    location: "Mediterranean · Miami Beach",
    locations: 1,
    heroMetric: { value: "46%", label: "reservations captured after-hours" },
    rotatingMetrics: [
      { value: "46%", label: "Reservations Captured After-Hours", sub: "hotel guests & late-night browsers booking while closed" },
      { value: "$68K", label: "Monthly Revenue Assisted", sub: "reservation capture for a single high-volume location" },
      { value: "100%", label: "Calls Answered", sub: "up from 61% — peak dinner service fully covered" },
      { value: "96%", label: "Guest Satisfaction", sub: "callers rated the experience positively" },
    ],
    timeline: [
      { week: "Week 1", event: "AI trained on menu & Chef Shani specials" },
      { week: "Week 2", event: "Live — overflow during peak dinner" },
      { week: "Month 1", event: "46% of reservations via AI after-hours" },
      { week: "Month 3", event: "Think Hospitality evaluating 20+ rollout" },
    ],
    audioFile: "/audio/case-studies/bella.mp3",
    audioLabel: "Reservation inquiry — Bella",
    audioDuration: "1:33",
    bgImage: "/case-studies/bg/bella.png",
    logo: "/case-studies/logos/bella.png",
  },
  {
    id: "canita",
    category: "Cuban Cuisine",
    emoji: "🍹",
    accent: "#1A7B7E",
    bg: "#1C1C1C",
    cardBg: "linear-gradient(135deg, #1C1C1C, #141D1E)",
    textColor: "#F5EDE0",
    subtextColor: "rgba(245,237,224,0.45)",
    borderColor: "rgba(26,123,126,0.25)",
    name: "La Cañita",
    subtitle: "Cuban-Caribbean · 3 Locations",
    location: "Kendall · South Beach · Bayside",
    locations: 3,
    heroMetric: { value: "$380K+", label: "revenue facilitated by AI" },
    rotatingMetrics: [
      { value: "$380K+", label: "Revenue Facilitated", sub: "1,679 reservations × 2.5 guests × $90 avg check" },
      { value: "5,500+", label: "Calls Answered in 6 Months", sub: "across Kendall + South Beach — zero voicemails" },
      { value: "45%", label: "Kendall Calls in Spanish", sub: "bilingual hospitality without staffing a Spanish-only host" },
      { value: "1,679", label: "Reservations Captured", sub: "every call routed into OpenTable — 4pm peak covered" },
    ],
    timeline: [
      { week: "Week 1", event: "Nacho trained on menus + OpenTable + Spanish + live music" },
      { week: "Month 1", event: "Live at Kendall — 45% of calls in Spanish from day one" },
      { week: "Month 3", event: "South Beach grand opening — 1,335 calls in December" },
      { week: "Month 6", event: "5,500 calls, 60% autonomous, 9 simultaneous at peak" },
    ],
    audioFile: "/audio/case-studies/canita.mp3",
    audioLabel: "Reservation + event inquiry — La Cañita Kendall",
    audioDuration: "1:18",
    bgImage: "/case-studies/bg/canita.png",
    logo: "/case-studies/logos/canita.png",
  },
];

export default function CaseStudySlide({ isMobile, onNext }) {
  const [selected, setSelected] = useState(null);
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setVisible(true);
      },
      { threshold: 0.2 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const goNext = () => {
    if (selected === null) return;
    if (selected < CASE_STUDIES.length - 1) {
      setSelected(selected + 1);
    } else {
      setSelected(null);
      onNext?.();
    }
  };

  const goPrev = () => {
    if (selected === null) return;
    if (selected > 0) setSelected(selected - 1);
    else setSelected(null);
  };

  useEffect(() => {
    if (selected === null) return;
    const handler = (e) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        goNext();
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "Escape") {
        setSelected(null);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selected]);

  const cs = selected !== null ? CASE_STUDIES[selected] : null;

  // Selection grid
  if (selected === null) {
    return (
      <Section innerRef={ref} style={{ background: BRAND.dark, overflow: "hidden" }}>
        <div
          style={{
            position: "absolute",
            width: 600,
            height: 600,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(249,115,22,0.04) 0%, transparent 70%)",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        />
        <div
          style={{
            maxWidth: 880,
            margin: "0 auto",
            width: "100%",
            zIndex: 1,
          }}
        >
          <div style={{ textAlign: "center", marginBottom: isMobile ? 24 : 32 }}>
            <div
              style={{
                fontSize: 13,
                letterSpacing: 3,
                color: BRAND.orange,
                textTransform: "uppercase",
                fontWeight: 600,
                marginBottom: 10,
              }}
            >
              Real Results
            </div>
            <h2
              style={{
                fontSize: isMobile ? 28 : 42,
                fontWeight: 800,
                color: BRAND.white,
                margin: 0,
                fontFamily: "'SF Pro Rounded', 'Manrope', sans-serif",
                lineHeight: 1.1,
              }}
            >
              Choose a case study
            </h2>
            <p
              style={{
                color: "rgba(255,255,255,0.4)",
                fontSize: isMobile ? 14 : 16,
                marginTop: 8,
              }}
            >
              Select the one that best matches your restaurant
            </p>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
              gap: 10,
            }}
          >
            {CASE_STUDIES.map((c, i) => (
              <button
                key={c.id}
                onClick={() => setSelected(i)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  background: c.cardBg,
                  border: `1px solid ${c.borderColor}`,
                  borderRadius: 16,
                  padding: "16px 18px",
                  cursor: "pointer",
                  width: "100%",
                  textAlign: "left",
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0)" : "translateY(14px)",
                  transition: `all 0.5s cubic-bezier(0.16,1,0.3,1) ${i * 0.07}s`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = `${c.accent}60`;
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = c.borderColor;
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <div
                  style={{
                    width: 46,
                    height: 46,
                    borderRadius: 12,
                    flexShrink: 0,
                    overflow: "hidden",
                    position: "relative",
                    background: `${c.accent}18`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 22,
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: `${c.bg}80`,
                    }}
                  />
                  <span style={{ position: "relative", zIndex: 1 }}>
                    {c.emoji}
                  </span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 2,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: c.textColor,
                      }}
                    >
                      {c.name}
                    </span>
                    <span
                      style={{
                        fontSize: 9.5,
                        padding: "2px 8px",
                        borderRadius: 100,
                        background: `${c.accent}20`,
                        color: c.accent,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {c.category}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 11.5,
                      color: c.subtextColor,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {c.subtitle}
                  </div>
                </div>
                <div
                  style={{
                    textAlign: "right",
                    flexShrink: 0,
                    paddingLeft: 8,
                  }}
                >
                  <div
                    style={{
                      fontSize: isMobile ? 18 : 22,
                      fontWeight: 800,
                      color: c.accent,
                    }}
                  >
                    {c.heroMetric.value}
                  </div>
                  <div
                    style={{
                      fontSize: 9.5,
                      color: c.subtextColor,
                      maxWidth: 90,
                      lineHeight: 1.2,
                    }}
                  >
                    {c.heroMetric.label}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </Section>
    );
  }

  // Expanded case study view
  return (
    <Section innerRef={ref} style={{ background: cs.bg, overflow: "hidden" }}>
      {cs.bgImage && (
        <>
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url(${cs.bgImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              opacity: 0.25,
              filter: "blur(0.8px)",
              zIndex: 0,
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: `linear-gradient(135deg, ${cs.bg}d4 0%, ${cs.bg}b3 50%, ${cs.accent}22 100%)`,
              zIndex: 0,
              pointerEvents: "none",
            }}
          />
        </>
      )}
      <div
        style={{
          position: "absolute",
          width: 700,
          height: 700,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${cs.accent}10 0%, transparent 70%)`,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          maxWidth: 920,
          margin: "0 auto",
          width: "100%",
          zIndex: 1,
        }}
      >
        {/* Back button */}
        <button
          onClick={() => setSelected(null)}
          style={{
            background: `${cs.accent}12`,
            border: `1px solid ${cs.accent}25`,
            borderRadius: 10,
            padding: "6px 14px",
            cursor: "pointer",
            color: cs.subtextColor,
            fontSize: 12,
            fontWeight: 500,
            marginBottom: 14,
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.background = `${cs.accent}25`)
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = `${cs.accent}12`)
          }
        >
          ← All Case Studies
        </button>

        {/* Header — simplified */}
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 4,
              flexWrap: "wrap",
            }}
          >
            {cs.logo ? (
              <img
                src={cs.logo}
                alt={cs.name}
                style={{
                  height: isMobile ? 34 : 42,
                  width: "auto",
                  objectFit: "contain",
                  filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.4))",
                }}
              />
            ) : (
              <span style={{ fontSize: 30 }}>{cs.emoji}</span>
            )}
            <h2
              style={{
                fontSize: isMobile ? 28 : 34,
                fontWeight: 800,
                color: cs.textColor,
                margin: 0,
                fontFamily: "'SF Pro Rounded', 'Manrope', sans-serif",
              }}
            >
              {cs.name}
            </h2>
            <span
              style={{
                fontSize: 10,
                padding: "3px 10px",
                borderRadius: 100,
                background: `${cs.accent}25`,
                color: cs.accent,
                fontWeight: 600,
              }}
            >
              {cs.category}
            </span>
          </div>
          <p
            style={{
              color: cs.subtextColor,
              fontSize: 13,
              margin: 0,
            }}
          >
            {cs.subtitle} · {cs.location}
          </p>
        </div>

        {/* Audio player */}
        <AudioPlayer
          accent={cs.accent}
          label={cs.audioLabel}
          duration={cs.audioDuration}
          bg={`${cs.accent}08`}
          audioSrc={cs.audioFile}
        />

        {/* Fixed 2x2 metrics grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(2, 1fr)",
            gap: isMobile ? 8 : 12,
            marginBottom: 14,
          }}
        >
          {cs.rotatingMetrics.map((m, i) => (
            <div
              key={i}
              style={{
                background: `${cs.accent}06`,
                border: `1px solid ${cs.borderColor}`,
                borderRadius: 16,
                padding: isMobile ? "16px 12px" : "22px 20px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: isMobile ? 26 : 38,
                  fontWeight: 900,
                  lineHeight: 1.05,
                  color: cs.accent,
                }}
              >
                {m.value}
              </div>
              <div
                style={{
                  fontSize: isMobile ? 11 : 13,
                  fontWeight: 700,
                  color: cs.textColor,
                  marginTop: 6,
                  lineHeight: 1.2,
                }}
              >
                {m.label}
              </div>
              <div
                style={{
                  fontSize: isMobile ? 10 : 11.5,
                  color: cs.subtextColor,
                  marginTop: 4,
                  lineHeight: 1.35,
                }}
              >
                {m.sub}
              </div>
            </div>
          ))}
        </div>

        {/* Timeline */}
        <div
          style={{
            background: `${cs.accent}05`,
            borderRadius: 12,
            border: `1px solid ${cs.borderColor}`,
            padding: isMobile ? "14px 16px" : "14px 20px",
            display: isMobile ? "grid" : "flex",
            gridTemplateColumns: isMobile ? "1fr 1fr" : undefined,
            gap: isMobile ? 12 : 0,
            alignItems: "stretch",
          }}
        >
          {cs.timeline.map((t, i) => (
            <div
              key={i}
              style={{
                flex: isMobile ? undefined : 1,
                paddingLeft: isMobile ? 0 : i === 0 ? 0 : 16,
                borderLeft: isMobile
                  ? "none"
                  : i === 0
                    ? "none"
                    : `1px solid ${cs.borderColor}`,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  color: cs.accent,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  marginBottom: 3,
                }}
              >
                {t.week}
              </div>
              <div
                style={{
                  fontSize: 11.5,
                  color: cs.subtextColor,
                  lineHeight: 1.4,
                }}
              >
                {t.event}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Prev / Next navigation chevrons */}
      <button
        onClick={goPrev}
        aria-label="Previous case study"
        style={{
          position: "absolute",
          left: isMobile ? 12 : 20,
          top: "50%",
          transform: "translateY(-50%)",
          width: isMobile ? 38 : 44,
          height: isMobile ? 38 : 44,
          borderRadius: "50%",
          border: `1px solid ${cs.accent}40`,
          background: `${cs.accent}10`,
          color: cs.accent,
          fontSize: isMobile ? 20 : 24,
          lineHeight: 1,
          cursor: "pointer",
          opacity: 0.45,
          transition: "all 0.2s ease",
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = "1";
          e.currentTarget.style.transform = "translateY(-50%) scale(1.08)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = "0.45";
          e.currentTarget.style.transform = "translateY(-50%) scale(1)";
        }}
      >
        ‹
      </button>
      <button
        onClick={goNext}
        aria-label={selected === CASE_STUDIES.length - 1 ? "Continue landing" : "Next case study"}
        style={{
          position: "absolute",
          right: isMobile ? 12 : 20,
          top: "50%",
          transform: "translateY(-50%)",
          width: isMobile ? 38 : 44,
          height: isMobile ? 38 : 44,
          borderRadius: "50%",
          border: `1px solid ${cs.accent}40`,
          background: `${cs.accent}10`,
          color: cs.accent,
          fontSize: isMobile ? 20 : 24,
          lineHeight: 1,
          cursor: "pointer",
          opacity: 0.45,
          transition: "all 0.2s ease",
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = "1";
          e.currentTarget.style.transform = "translateY(-50%) scale(1.08)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = "0.45";
          e.currentTarget.style.transform = "translateY(-50%) scale(1)";
        }}
      >
        {selected === CASE_STUDIES.length - 1 ? "↓" : "›"}
      </button>
    </Section>
  );
}
